import Margin from "~/components/Margin";
import { useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Input,
  InputCurrency,
  InputSelect,
} from "prosperita-dumbo-react";
import "prosperita-dumbo-react/dist/style.css";

import lockIcon from "~/assets/lock.png";
import styled from "styled-components";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  convertStringToNumber,
  onlyNumbers,
  validateEmail,
} from "~/services/utils";

import useLoading from "~/hooks/useLoading";

import { toast } from "react-toastify";
import {
  bussinesSectorOptions,
  cnaeOptions,
  entityOptions,
  residenceOptions,
  ufOptions,
} from "~/utils/options";
import {
  getPartnersListAPI,
  IPartner,
  postPartnerCompanyAPI,
} from "~/services/api/partner";
import { getCepAPI } from "~/services/api/cep";
import { isValidFullNameCompany } from "~/services/validators";
import Stepper from "~/components/Stepper";
import documentNumberValidator from "~/services/validateDocumentNumber";
import { isPast } from "date-fns";
import { getErrorMessage, getErrorMessages } from "~/services/api/error";
import ArrowIcon from "~/assets/arrow-right-_1_.png";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: start;
  padding: 30px 50px;
`;

const TagLup = styled.img`
  object-fit: contain;
  width: 200px;
  margin: 20px 0;
`;

const Text = styled.h4`
  color: #161616;
  font-family: "IBM Plex Sans";
  font-size: 21px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  margin-top: 20px;
`;

const Subtext = styled.h4`
  color: #161616;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  margin-top: 20px;
`;

type Values = {
  name: string;
  owner_uuid: string;
  document_number: string;
  document_type: string;
  contact_email: string;
  contact_phone: string;
  foundation_date: string;
  cnae: string;
  fantasy_name: string;
  legal_nature: string;
  municipal_registration: string;
  state_registration: string;
  website: string;
  business_sector: string;
  employer_code: string;
  b3_account: string;
  address_zip_code: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_district: string;
  address_city: string;
  address_state: string;
  address_country: string;
  address_type: string;
  finantial_info_initial_share_capital: string;
  finantial_info_net_equity: string;
  finantial_info_number_of_employees: string;
  finantial_info_average_monthly_revenue: string;
  finantial_info_average_gross_profit: string;
  finantial_info_exportation_percentage: string;
  finantial_info_suppliers_cost: string;
  finantial_info_tax_cost: string;
  finantial_info_last_12_months_revenue: string;
  finantial_info_leased_or_financed_value: string;
  finantial_info_arrangement: string;
  finantial_info_payroll_value: string;
  finantial_info_headquarters_location: string;
  finantial_info_pmv: string;
  finantial_info_funding_potential: string;
  finantial_info_late_sales_percentage: string;
  profile: string;
};

const initialValues: Values = {
  name: "",
  owner_uuid: "",
  document_number: "",
  document_type: "",
  contact_email: "",
  contact_phone: "",
  foundation_date: "",
  cnae: "",
  fantasy_name: "",
  legal_nature: "",
  municipal_registration: "",
  state_registration: "",
  website: "",
  business_sector: "",
  employer_code: "",
  b3_account: "",
  address_zip_code: "",
  address_street: "",
  address_number: "",
  address_complement: "",
  address_district: "",
  address_city: "",
  address_state: "",
  address_country: "",
  address_type: "",
  finantial_info_initial_share_capital: "",
  finantial_info_net_equity: "",
  finantial_info_number_of_employees: "",
  finantial_info_average_monthly_revenue: "",
  finantial_info_average_gross_profit: "",
  finantial_info_exportation_percentage: "",
  finantial_info_suppliers_cost: "",
  finantial_info_tax_cost: "",
  finantial_info_last_12_months_revenue: "",
  finantial_info_leased_or_financed_value: "",
  finantial_info_arrangement: "",
  finantial_info_payroll_value: "",
  finantial_info_headquarters_location: "",
  finantial_info_pmv: "",
  finantial_info_funding_potential: "",
  finantial_info_late_sales_percentage: "",
  profile: "",
};

const NewContactCompany = () => {
  const navigate = useNavigate();
  const [errorCep, setErrorCep] = useState<boolean>(false);
  const { setLoading } = useLoading();
  // removed unused local error state
  const [partnersData, setPartnersData] = useState<IPartner[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [fieldErrorMessages, setFieldErrorMessages] = useState<
    Record<string, string>
  >({});

  const { values, setFieldValue } = useFormik({
    initialValues,
    onSubmit: () => undefined,
  });

  const canDoSimulations = true;

  const steps = [
    {
      label: "Informações",
      isCompleted: false,
      isActive: true,
    },
    {
      label: "Dados bancários",
      isCompleted: false,
      isActive: false,
    },
  ];

  const partnersOptions = useMemo(() => {
    return partnersData
      ?.map((partner: IPartner) => ({
        value: partner.uuid,
        label: partner.name,
      }))

      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [partnersData]);

  const isValidCNPJ = useMemo(() => {
    return (
      values.document_number.length > 13 &&
      documentNumberValidator(values.document_number)
    );
  }, [values.document_number]);

  const validateRequiredFields = useCallback((): boolean => {
    const errors: Record<string, boolean> = {};
    const messages: Record<string, string> = {};
    const fieldOrder = [
      "document_number",
      "name",
      "fantasy_name",
      "legal_nature",
      "foundation_date",
      "cnae",
      "business_sector",
      "contact_email",
      "contact_phone",
      "address_zip_code",
      "address_street",
      "address_number",
      "address_district",
      "address_city",
      "address_state",
      "address_type",
    ];

    // Campos obrigatórios
    if (!values.document_number || values.document_number.trim() === "") {
      errors.document_number = true;
      messages.document_number = "campo obrigatório";
    }

    if (!values.name || values.name.trim() === "") {
      errors.name = true;
      messages.name = "campo obrigatório";
    } else if (!isValidFullNameCompany(values.name)) {
      errors.name = true;
      messages.name = "Razão social inválida";
    }

    if (!values.fantasy_name || values.fantasy_name.trim() === "") {
      errors.fantasy_name = true;
      messages.fantasy_name = "campo obrigatório";
    }
    if (!values.legal_nature || values.legal_nature === "") {
      errors.legal_nature = true;
      messages.legal_nature = "campo obrigatório";
    }
    if (!values.foundation_date || values.foundation_date.trim() === "") {
      errors.foundation_date = true;
      messages.foundation_date = "campo obrigatório";
    } else {
      const year = Number(values.foundation_date.slice(0, 4));
      if (!Number.isFinite(year) || year < 1900) {
        errors.foundation_date = true;
      }
    }
    if (!values.cnae || values.cnae === "") {
      errors.cnae = true;
      messages.cnae = "campo obrigatório";
    }
    if (!values.business_sector || values.business_sector === "") {
      errors.business_sector = true;
      messages.business_sector = "campo obrigatório";
    }
    if (!values.contact_email || values.contact_email.trim() === "") {
      errors.contact_email = true;
      messages.contact_email = "campo obrigatório";
    }
    if (!values.contact_phone || values.contact_phone.trim() === "") {
      errors.contact_phone = true;
      messages.contact_phone = "campo obrigatório";
    }
    if (!values.address_zip_code || values.address_zip_code.trim() === "") {
      errors.address_zip_code = true;
      messages.address_zip_code = "campo obrigatório";
    }
    if (!values.address_street || values.address_street.trim() === "") {
      errors.address_street = true;
      messages.address_street = "campo obrigatório";
    }
    if (!values.address_number || values.address_number.trim() === "") {
      errors.address_number = true;
      messages.address_number = "campo obrigatório";
    }
    if (!values.address_district || values.address_district.trim() === "") {
      errors.address_district = true;
      messages.address_district = "campo obrigatório";
    }
    if (!values.address_city || values.address_city.trim() === "") {
      errors.address_city = true;
      messages.address_city = "campo obrigatório";
    }
    if (!values.address_state || values.address_state === "") {
      errors.address_state = true;
      messages.address_state = "campo obrigatório";
    }
    if (!values.address_type || values.address_type === "") {
      errors.address_type = true;
      messages.address_type = "campo obrigatório";
    }

    // Validação CEP: precisa de 8 dígitos
    const cepDigits = onlyNumbers(values.address_zip_code || "");
    if (cepDigits.length !== 8) {
      errors.address_zip_code = true;
      if (!messages.address_zip_code) {
        messages.address_zip_code = "CEP inválido";
      }
    }

    setFieldErrors(errors);
    setFieldErrorMessages(messages);

    // Encontra o primeiro campo com erro e faz scroll até ele
    if (Object.keys(errors).length > 0) {
      const firstErrorField = fieldOrder.find((field) => errors[field]);
      if (firstErrorField) {
        // Aguarda um pouco para garantir que o erro foi renderizado
        setTimeout(() => {
          // Tenta encontrar o campo pelo name primeiro
          let fieldElement = document.querySelector(
            `[name="${firstErrorField}"]`
          );

          // Se não encontrou, tenta encontrar pelo container ou label próximo
          if (!fieldElement) {
            const allInputs = document.querySelectorAll("input, select");
            for (const input of Array.from(allInputs)) {
              const name = input.getAttribute("name");
              if (name === firstErrorField) {
                fieldElement = input.closest("div") || input;
                break;
              }
            }
          }

          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            // Foca no campo se for um input ou select
            if (fieldElement instanceof HTMLElement) {
              const inputElement =
                fieldElement.querySelector("input") ||
                fieldElement.querySelector("select") ||
                (fieldElement.tagName === "INPUT" ||
                fieldElement.tagName === "SELECT"
                  ? fieldElement
                  : null);
              if (inputElement) {
                (inputElement as HTMLElement).focus();
              }
            }
          }
        }, 100);
      }
    }

    return Object.keys(errors).length === 0;
  }, [values]);

  const createContactCompany = useCallback(async () => {
    // Valida campos obrigatórios
    if (!validateRequiredFields()) {
      return;
    }

    // Valida CNPJ incompleto ou inválido antes de enviar
    const cnpjLen = values.document_number.length;
    if (cnpjLen > 0 && cnpjLen < 18) {
      setFieldErrors((prev) => ({ ...prev, document_number: true }));
      // força o scroll e foco no campo
      setTimeout(() => {
        const el = document.querySelector('[name="document_number"]') as
          | HTMLInputElement
          | null;
        el?.focus();
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }
    if (cnpjLen >= 18 && !isValidCNPJ) {
      setFieldErrors((prev) => ({ ...prev, document_number: true }));
      setTimeout(() => {
        const el = document.querySelector('[name="document_number"]') as
          | HTMLInputElement
          | null;
        el?.focus();
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }

    // Validação Data de abertura: não pode ser futura nem inválida
    if (
      values.foundation_date &&
      values.foundation_date.length > 9 &&
      !isPast(values.foundation_date)
    ) {
      setFieldErrors((prev) => ({ ...prev, foundation_date: true }));
      setTimeout(() => {
        const el = document.querySelector('[name="foundation_date"]') as
          | HTMLInputElement
          | null;
        el?.focus();
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }
    if (values.foundation_date) {
      const year = Number(values.foundation_date.slice(0, 4));
      if (!Number.isFinite(year) || year < 1900) {
        setFieldErrors((prev) => ({ ...prev, foundation_date: true }));
        setTimeout(() => {
          const el = document.querySelector('[name="foundation_date"]') as HTMLInputElement | null;
          el?.focus();
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 0);
        return;
      }
    }

    // Validação de e-mail
    if (!validateEmail(values.contact_email)) {
      setFieldErrors((prev) => ({ ...prev, contact_email: true }));
      setTimeout(() => {
        const el = document.querySelector('[name="contact_email"]') as
          | HTMLInputElement
          | null;
        el?.focus();
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }

    // Validação de celular: 11 dígitos e iniciando com 9 após o DDD
    {
      const digits = onlyNumbers(values.contact_phone || "");
      if (!digits || digits.length !== 11 || digits[2] !== "9") {
      setFieldErrors((prev) => ({ ...prev, contact_phone: true }));
      setTimeout(() => {
        const el = document.querySelector('[name="contact_phone"]') as
          | HTMLInputElement
          | null;
        el?.focus();
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
      }
    }

    // Criação do objeto de payload
    const payload = {
      ...(values.owner_uuid !== "" && { owner_uuid: values.owner_uuid }),
      name: values.name,
      document_number: onlyNumbers(values.document_number),
      document_type: "company",
      contact: {
        email: values.contact_email,
        phone: onlyNumbers(values.contact_phone),
      },
      foundation_date: values.foundation_date,
      cnae: values.cnae,
      fantasy_name: values.fantasy_name,
      legal_nature: values.legal_nature,
      municipal_registration: values.municipal_registration,
      state_registration: values.state_registration,
      website: values.website,
      business_sector: values.business_sector,
      employer_code: values.employer_code,
      b3_account: values.b3_account,
      address: {
        zip_code: onlyNumbers(values.address_zip_code),
        street: values.address_street,
        number: values.address_number,
        ...(values.address_complement && { complement: values.address_complement }),
        district: values.address_district,
        city: values.address_city,
        state: values.address_state,
        country: values.address_country || "Brasil",
        type: values.address_type,
      },
      finantial_info: {
        ...(values.finantial_info_initial_share_capital && {
          initial_share_capital: convertStringToNumber(
            values.finantial_info_initial_share_capital
          ),
        }),
        ...(values.finantial_info_net_equity && {
          net_equity: convertStringToNumber(values.finantial_info_net_equity),
        }),
        ...(values.finantial_info_number_of_employees && {
          number_of_employees: Number(
            values.finantial_info_number_of_employees
          ),
        }),
        ...(values.finantial_info_average_monthly_revenue && {
          average_monthly_revenue: convertStringToNumber(
            values.finantial_info_average_monthly_revenue
          ),
        }),
        ...(values.finantial_info_average_gross_profit && {
          average_gross_profit: convertStringToNumber(
            values.finantial_info_average_gross_profit
          ),
        }),
        ...(values.finantial_info_exportation_percentage && {
          exportation_percentage: Number(
            values.finantial_info_exportation_percentage
          ),
        }),
        ...(values.finantial_info_suppliers_cost && {
          suppliers_cost: convertStringToNumber(
            values.finantial_info_suppliers_cost
          ),
        }),
        ...(values.finantial_info_tax_cost && {
          tax_cost: convertStringToNumber(values.finantial_info_tax_cost),
        }),
        ...(values.finantial_info_last_12_months_revenue && {
          last_12_months_revenue: convertStringToNumber(
            values.finantial_info_last_12_months_revenue
          ),
        }),
        ...(values.finantial_info_leased_or_financed_value && {
          leased_or_financed_value: convertStringToNumber(
            values.finantial_info_leased_or_financed_value
          ),
        }),
        ...(values.finantial_info_arrangement && {
          arrangement: convertStringToNumber(values.finantial_info_arrangement),
        }),
        ...(values.finantial_info_payroll_value && {
          payroll_value: convertStringToNumber(
            values.finantial_info_payroll_value
          ),
        }),
        ...(values.finantial_info_headquarters_location && {
          headquarters_location: values.finantial_info_headquarters_location,
        }),
        ...(values.finantial_info_pmv && {
          pmv: convertStringToNumber(values.finantial_info_pmv),
        }),
        ...(values.finantial_info_funding_potential && {
          funding_potential: Number(values.finantial_info_funding_potential),
        }),
        ...(values.finantial_info_late_sales_percentage && {
          late_sales_percentage: Number(
            values.finantial_info_late_sales_percentage
          ),
        }),
      },
      type: "contact",
    };

    setLoading(true);

    try {
      const { data } = await postPartnerCompanyAPI(payload);

      localStorage.setItem("@:partner_uuid", data.payload.uuid);
      toast.success("Contato Pessoa Jurídica criado com sucesso");
      setLoading(false);
      navigate(`/app/contacts/new/company/${data.payload.uuid}/bank-data`);
    } catch (e) {
      console.log(e);
      setLoading(false);
      const messages = getErrorMessages(e);
      if (messages.length > 1) {
        messages.forEach((m) => toast.error(m));
      } else {
        const message = getErrorMessage(
          e,
          "Não foi possível criar o Contato Pessoa Jurídica. Tente novamente mais tarde"
        );
        toast.error(message);
      }
    }
  }, [values, setLoading, navigate, validateRequiredFields]);

  const fetchPartners = useCallback(async (): Promise<void> => {
    try {
      const { data } = await getPartnersListAPI({
        document_type: "person",
      });

      setPartnersData(data.payload.reverse());
    } catch (e) {
      console.error("Erro ao buscar contatos:", e);
    }
  }, [setPartnersData]);

  const fetchCEP = useCallback(
    async (cep: string): Promise<void> => {
      try {
        const { data } = await getCepAPI(cep);

        if (data.erro) {
          setErrorCep(true);
          setFieldValue("address_street", "");
          setFieldValue("address_district", "");
          setFieldValue("address_city", "");
          setFieldValue("address_state", "Selecione");
        } else {
          setErrorCep(false);
          setFieldValue("address_street", data.logradouro);
          setFieldValue("address_district", data.bairro);
          setFieldValue("address_city", data.localidade);
          setFieldValue("address_state", data.uf);
          // limpa erros de campos preenchidos pelo ViaCEP
          setFieldErrors((prev) => ({
            ...prev,
            address_zip_code: false,
            address_street: false,
            address_district: false,
            address_city: false,
            address_state: false,
          }));
        }
      } catch (e) {
        console.error("Erro ao buscar contatos:", e);
        setErrorCep(false);
      }
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (values.address_zip_code.length === 9) {
      fetchCEP(onlyNumbers(values.address_zip_code));
    }
  }, [values.address_zip_code, fetchCEP]);

  useEffect(() => {
    if (values.address_zip_code.length === 8) {
      setErrorCep(false);
    }
  }, [values.address_zip_code]);

  useEffect(() => {
    localStorage.removeItem("@:partner_uuid");
    fetchPartners();
  }, [fetchPartners]);



  return (
    <Container>
      <Breadcrumb
        crumbsOptions={[
          {
            crumb: "Contatos",
            onClick: () => navigate("/app/contacts"),
          },
          { crumb: "Novo Contato" },
        ]}
        endHiddenIndex={5}
        startHiddenIndex={5}
      />

      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* <h2
            style={{
              margin: "10px 0 0 45px ",
              color: "#000",
              fontSize: "40px",
              fontStyle: "normal",
              fontWeight: 100,
              lineHeight: "40px",
            }}
          >
          Operações de crédito
          </h2> */}
        </div>
      </div>
      <div style={{ width: "100%" }}>
        <>
          {canDoSimulations && (
            <>
              <>
                <Margin mb={30} />
                <div
                  style={{
                    position: "sticky",
                    top: 113,

                    gap: "10px",
                    width: "100%",
                    zIndex: "3",
                    padding: "10px",
                    background: "#fff",
                  }}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Button
                      kind={"ghost"}
                      label="Cancelar"
                      size="large"
                      onClick={() => navigate("/app/contacts")}
                    />
                    <div style={{ display: "flex", gap: "10px" }}>
                      {/* <Button
                        kind={"tertiary"}
                        label="Voltar"
                        size="large"
                        onClick={() =>
                          navigate("/app/contacts/new/select-type")
                        }
                      /> */}
                      <Button
                        kind="primary"
                        label="Avançar"
                        size="large"
                        icon={ArrowIcon}
                        onClick={() => createContactCompany()}
                      />
                    </div>
                  </div>
                  <Stepper steps={steps} />
                  <Margin mb={10} />
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <Margin mb={30} />

                    <Text style={{ fontSize: "25px" }}>
                      Novo contato Pessoa Jurídica{" "}
                    </Text>
                    <Margin mb={20} />
                    <h4>Dados da empresa</h4>
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr 1fr",
                    gap: "32px",
                    rowGap: "30px",
                    marginTop: "20px",
                  }}
                >
                  <>
                    <Input
                      size="large"
                      name="document_number"
                      label="CNPJ (Obrigatório)"
                      placeholder="00.000.000/0000-00"
                      value={values.document_number}
                      maxLength={18}
                      error={
                        (values.document_number.length > 0 &&
                          values.document_number.length < 18) ||
                        (values.document_number.length >= 18 && !isValidCNPJ) ||
                        fieldErrors.document_number
                      }
                      message={
                        values.document_number.length > 0 &&
                        values.document_number.length < 18
                          ? "CNPJ incompleto"
                          : values.document_number.length >= 18 && !isValidCNPJ
                          ? "CNPJ inválido"
                          : fieldErrors.document_number
                          ? "campo obrigatório"
                          : ""
                      }
                      onChange={(e) => {
                        let value = e.target.value;
                        value = value.replace(/[^\d]/g, "");
                        if (value.length <= 2) {
                          value = `${value}`;
                        } else if (value.length <= 5) {
                          value = `${value.slice(0, 2)}.${value.slice(2)}`;
                        } else if (value.length <= 8) {
                          value = `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
                        } else if (value.length <= 12) {
                          value = `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8)}`;
                        } else if (value.length <= 14) {
                          value = `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12)}`;
                        }

                        setFieldValue("document_number", value);
                        if (fieldErrors.document_number && value.trim() !== "") {
                          setFieldErrors((prev) => ({
                            ...prev,
                            document_number: false,
                          }));
                        }
                      }}
                    />
                    <Input
                      size="large"
                      name="name"
                      label="Razão Social (Obrigatório)"
                      placeholder="Digite"
                      value={values.name}
                      error={fieldErrors.name}
                      message={
                        fieldErrors.name
                          ? fieldErrorMessages.name || "campo obrigatório"
                          : ""
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFieldValue("name", newValue);

                        if (newValue.trim() && isValidFullNameCompany(newValue)) {
                          setFieldErrors((prev) => ({ ...prev, name: false }));
                          setFieldErrorMessages((prev) => ({
                            ...prev,
                            name: "",
                          }));
                        }
                      }}
                    />
                    <Input
                      size="large"
                      name="fantasy_name"
                      label="Nome Fantasia (Obrigatório)"
                      placeholder="Digite"
                      value={values.fantasy_name}
                      error={fieldErrors.fantasy_name}
                      message={fieldErrors.fantasy_name ? "campo obrigatório" : ""}
                      onChange={(e) => {
                        setFieldValue("fantasy_name", e.target.value);
                        if (fieldErrors.fantasy_name && e.target.value.trim() !== "") {
                          setFieldErrors((prev) => ({ ...prev, fantasy_name: false }));
                        }
                      }}
                    />
                  </>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "32px",
                    rowGap: "30px",
                    marginTop: "20px",
                  }}
                >
                  <>
                    <div>
                      <InputSelect
                        size="small"
                        kind="default"
                        name="legal_nature"
                        label="Natureza Jurídica (Obrigatório)"
                        options={entityOptions}
                        placeholder="Escolha uma opção"
                        error={fieldErrors.legal_nature}
                        message={fieldErrors.legal_nature ? "campo obrigatório" : ""}
                        onChange={(e) => {
                          setFieldValue("legal_nature", e.value);
                          if (fieldErrors.legal_nature && e.value) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              legal_nature: false,
                            }));
                          }
                        }}
                      />
                    </div>
                    <Input
                      size="large"
                      type="date"
                      name="foundation_date"
                      label="Data de abertura (Obrigatório)"
                      placeholder="dd/mm/aaaa"
                      error={
                        (values.foundation_date?.length > 9 &&
                          !isPast(values.foundation_date)) ||
                        (values.foundation_date?.length > 9 &&
                          (Number(values.foundation_date.slice(0, 4)) < 1900 ||
                            !Number.isFinite(
                              Number(values.foundation_date.slice(0, 4))
                            ))) ||
                        fieldErrors.foundation_date
                      }
                      message={
                        values.foundation_date?.length > 9 &&
                        !isPast(values.foundation_date)
                          ? "A data de fundação não pode ser uma data futura"
                          : values.foundation_date?.length > 9 &&
                            (Number(values.foundation_date.slice(0, 4)) < 1900 ||
                              !Number.isFinite(
                                Number(values.foundation_date.slice(0, 4))
                              ))
                          ? "Data inválida"
                          : fieldErrors.foundation_date
                          ? "campo obrigatório"
                          : ""
                      }
                      value={values.foundation_date}
                      onChange={(e) => {
                        setFieldValue("foundation_date", e.target.value);
                        if (
                          fieldErrors.foundation_date &&
                          e.target.value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            foundation_date: false,
                          }));
                        }
                      }}
                    />
                    <div>
                      <InputSelect
                        size="small"
                        kind="default"
                        name="cnae"
                        label="CNAE (Obrigatório)"
                        placeholder="Escolha uma opção"
                        options={cnaeOptions}
                        error={fieldErrors.cnae}
                        message={fieldErrors.cnae ? "campo obrigatório" : ""}
                        onChange={(e) => {
                          setFieldValue("cnae", e.value);
                          if (fieldErrors.cnae && e.value) {
                            setFieldErrors((prev) => ({ ...prev, cnae: false }));
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputSelect
                        size="small"
                        kind="default"
                        name="business_sector"
                        label="Ramo de atividade (Obrigatório)"
                        placeholder="Escolha uma opção"
                        options={bussinesSectorOptions}
                        error={fieldErrors.business_sector}
                        message={
                          fieldErrors.business_sector ? "campo obrigatório" : ""
                        }
                        onChange={(e) => {
                          setFieldValue("business_sector", e.value);
                          if (fieldErrors.business_sector && e.value) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              business_sector: false,
                            }));
                          }
                        }}
                      />
                    </div>
                    <Input
                      max={20}
                      maxLength={20}
                      size="large"
                      name="municipal_registration"
                      label="Inscrição Municipal"
                      placeholder="00000000000"
                      value={values.municipal_registration}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setFieldValue("municipal_registration", value);
                      }}
                    />
                    <Input
                      max={20}
                      maxLength={20}
                      size="large"
                      name="state_registration"
                      label="Inscrição Estadual"
                      placeholder="00000000000"
                      value={values.state_registration}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setFieldValue("state_registration", value);
                      }}
                    />

                    <div>
                      <InputSelect
                        size="small"
                        kind="default"
                        name="employer_code"
                        label="Gestor da empresa"
                        placeholder="Escolha uma opção"
                        options={partnersOptions}
                        onChange={(e) =>
                          setFieldValue("employer_code", e.value)
                        }
                      />
                    </div>
                    <Input
                      max={50}
                      maxLength={50}
                      size="large"
                      name="employer_code"
                      label="Código de empregador"
                      placeholder="Digite"
                      value={values.employer_code}
                      onChange={(e) =>
                        setFieldValue("employer_code", e.target.value)
                      }
                    />
                    <Input
                      size="large"
                      name="website"
                      label="Website"
                      placeholder="Digite"
                      value={values.website}
                      onChange={(e) => setFieldValue("website", e.target.value)}
                    />
                    <Input
                      max={25}
                      maxLength={25}
                      size="large"
                      name="b3_account"
                      label="Conta B3"
                      placeholder="Digite"
                      value={values.b3_account}
                      onChange={(e) =>
                        setFieldValue("b3_account", e.target.value)
                      }
                    />
                  </>
                </div>
                <Margin mt={50} />
              </>
              <>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <h4>Dados de contato</h4>
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: "32px",
                    rowGap: "30px",
                    marginTop: "20px",
                  }}
                >
                  <>
                    <Input
                      size="large"
                      name="contact_email"
                      label="E-mail (Obrigatório)"
                      placeholder="contato@mail.com"
                      value={values.contact_email}
                      error={
                        fieldErrors.contact_email ||
                        (!!values.contact_email &&
                          !validateEmail(values.contact_email))
                      }
                      message={
                        fieldErrors.contact_email
                          ? "campo obrigatório"
                          : !!values.contact_email &&
                            !validateEmail(values.contact_email)
                          ? values.contact_email.length < 5
                            ? "E-mail incompleto"
                            : "E-mail inválido"
                          : ""
                      }
                      onChange={(e) => {
                        setFieldValue("contact_email", e.target.value);
                        if (
                          fieldErrors.contact_email &&
                          e.target.value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            contact_email: false,
                          }));
                        }
                      }}
                    />
                    <Input
                      size="large"
                      maxLength={15}
                      name="contact_phone"
                      label="Celular (Obrigatório)"
                      placeholder="(00) 00000-0000"
                      value={values.contact_phone}
                      error={
                        fieldErrors.contact_phone ||
                        (!!values.contact_phone &&
                          (onlyNumbers(values.contact_phone).length !== 11 ||
                            onlyNumbers(values.contact_phone)[2] !== "9"))
                      }
                      message={
                        fieldErrors.contact_phone
                          ? "campo obrigatório"
                          : !!values.contact_phone &&
                            onlyNumbers(values.contact_phone).length < 11
                          ? "Celular incompleto"
                          : !!values.contact_phone &&
                            onlyNumbers(values.contact_phone).length === 11 &&
                            onlyNumbers(values.contact_phone)[2] !== "9"
                          ? "Celular inválido"
                          : ""
                      }
                      onChange={(e) => {
                        let only = e.target.value.replace(/[^\d]/g, "");
                        if (only.length === 0) {
                          setFieldValue("contact_phone", "");
                          return;
                        }
                        let value = "";
                        if (only.length <= 2) {
                          value = `(${only}`;
                        } else if (only.length <= 7) {
                          value = `(${only.slice(0, 2)}) ${only.slice(2)}`;
                        } else if (only.length <= 10) {
                          // ainda digitando, sem hífen para não travar backspace
                          value = `(${only.slice(0, 2)}) ${only.slice(2, 7)}${only.slice(7)}`;
                        } else {
                          value = `(${only.slice(0, 2)}) ${only.slice(2, 7)}-${only.slice(7, 11)}`;
                        }

                        setFieldValue("contact_phone", value);
                        if (
                          fieldErrors.contact_phone &&
                          value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            contact_phone: false,
                          }));
                        }
                      }}
                    />
                  </>
                </div>
                <Margin mt={50} />
              </>
              <>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <h4>Endereço</h4>
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "32px",
                    rowGap: "30px",
                    marginTop: "20px",
                  }}
                >
                  <>
                    <Input
                      size="large"
                      name="address_zip_code"
                      max={9}
                      maxLength={9}
                      label="CEP (Obrigatório)"
                      placeholder="Digite"
                      value={values.address_zip_code}
                      error={
                        (errorCep && values.address_zip_code.length === 9) ||
                        fieldErrors.address_zip_code
                      }
                      message={
                        errorCep && values.address_zip_code.length === 9
                          ? "CEP inválido"
                          : fieldErrors.address_zip_code
                          ? "campo obrigatório"
                          : ""
                      }
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d]/g, "");

                        if (value.length <= 5) {
                          value = `${value.slice(0, 5)}`;
                        } else if (value.length <= 8) {
                          value = `${value.slice(0, 5)}-${value.slice(5, 8)}`;
                        }

                        setFieldValue("address_zip_code", value);
                        if (
                          fieldErrors.address_zip_code &&
                          value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            address_zip_code: false,
                          }));
                        }
                      }}
                    />
                    <div style={{ gridColumn: "span 2" }}>
                      <Input
                        size="large"
                        name="address_street"
                        label="Endereço (Obrigatório)"
                        disabled={!values.address_zip_code}
                        placeholder="Digite"
                        value={values.address_street}
                        error={fieldErrors.address_street}
                        message={fieldErrors.address_street ? "campo obrigatório" : ""}
                        onChange={(e) => {
                          setFieldValue("address_street", e.target.value);
                          if (
                            fieldErrors.address_street &&
                            e.target.value.trim() !== ""
                          ) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              address_street: false,
                            }));
                          }
                        }}
                      />
                    </div>
                    <Input
                      size="large"
                      name="address_number"
                      label="Número (Obrigatório)"
                      placeholder="Digite"
                      disabled={!values.address_zip_code}
                      value={values.address_number}
                      error={fieldErrors.address_number}
                      message={fieldErrors.address_number ? "campo obrigatório" : ""}
                      onChange={(e) => {
                        setFieldValue("address_number", e.target.value);
                        if (
                          fieldErrors.address_number &&
                          e.target.value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            address_number: false,
                          }));
                        }
                      }}
                    />
                    <Input
                      size="large"
                      name="address_complement"
                      label="Complemento"
                      placeholder="Apartamento, bloco, referência..."
                      disabled={!values.address_zip_code}
                      value={values.address_complement}
                      onChange={(e) => setFieldValue("address_complement", e.target.value)}
                    />
                    <Input
                      size="large"
                      name="address_district"
                      label="Bairro (Obrigatório)"
                      placeholder="Digite"
                      disabled={!values.address_zip_code}
                      value={values.address_district}
                      error={fieldErrors.address_district}
                      message={fieldErrors.address_district ? "campo obrigatório" : ""}
                      onChange={(e) => {
                        setFieldValue("address_district", e.target.value);
                        if (
                          fieldErrors.address_district &&
                          e.target.value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            address_district: false,
                          }));
                        }
                      }}
                    />
                    <Input
                      size="large"
                      name="address_city"
                      label="Cidade (Obrigatório)"
                      placeholder="Digite"
                      disabled={!values.address_zip_code}
                      value={values.address_city}
                      error={fieldErrors.address_city}
                      message={fieldErrors.address_city ? "campo obrigatório" : ""}
                      onChange={(e) => {
                        setFieldValue("address_city", e.target.value);
                        if (
                          fieldErrors.address_city &&
                          e.target.value.trim() !== ""
                        ) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            address_city: false,
                          }));
                        }
                      }}
                    />
                    <div>
                      <InputSelect
                        size="small"
                        kind="default"
                        name="address_state"
                        disabled={!values.address_zip_code}
                        label="Estado (Obrigatório)"
                        placeholder={
                          values.address_state ? values.address_state : "UF"
                        }
                        options={ufOptions}
                        error={fieldErrors.address_state}
                        message={fieldErrors.address_state ? "campo obrigatório" : ""}
                        onChange={(e) => {
                          setFieldValue("address_state", e.value);
                          if (fieldErrors.address_state && e.value) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              address_state: false,
                            }));
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputSelect
                        size="small"
                        kind="default"
                        disabled={!values.address_zip_code}
                        name="address_type"
                        label="Tipo de residência (Obrigatório)"
                        placeholder="Escolha uma opção"
                        options={residenceOptions}
                        error={fieldErrors.address_type}
                        message={fieldErrors.address_type ? "campo obrigatório" : ""}
                        onChange={(e) => {
                          setFieldValue("address_type", e.value);
                          if (fieldErrors.address_type && e.value) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              address_type: false,
                            }));
                          }
                        }}
                      />
                    </div>
                  </>
                </div>
                <Margin mt={50} />
              </>
              <>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <h4>Dados financeiros</h4>
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: "32px",
                    rowGap: "30px",
                    marginTop: "20px",
                  }}
                >
                  <>
                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_average_monthly_revenue"
                      label="Faturamento médio mensal"
                      placeholder="Digite"
                      message="Último 12 meses"
                      value={values.finantial_info_average_monthly_revenue}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_average_monthly_revenue",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_average_gross_profit"
                      label="Lucro bruto médio"
                      placeholder="Digite"
                      value={values.finantial_info_average_gross_profit}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_average_gross_profit",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_initial_share_capital"
                      label="Capital social inicial"
                      placeholder="Digite"
                      value={values.finantial_info_initial_share_capital}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_initial_share_capital",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_net_equity"
                      label="Patrimônio Líquido"
                      placeholder="Digite"
                      value={values.finantial_info_net_equity}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_net_equity",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_arrangement"
                      label="Faturamento médio mensal"
                      placeholder="Digite"
                      message="Arranjo"
                      value={values.finantial_info_arrangement}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_arrangement",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_leased_or_financed_value"
                      label="Valor de Bens Arrendados ou Financiados"
                      placeholder="Digite"
                      value={values.finantial_info_leased_or_financed_value}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_leased_or_financed_value",
                          e.target.value
                        )
                      }
                    />

                    <Input
                      size="large"
                      name="finantial_info_exportation_percentage"
                      label="Percentual de Exportação (%)"
                      placeholder="Digite"
                      max={6}
                      value={values.finantial_info_exportation_percentage}
                      maxLength={6}
                      onChange={(e) => {
                        let value = e.target.value;
                        value = value.replace(/[^\d.]/g, "");

                        const hasDot = value.includes(".");
                        if (hasDot && (value.match(/\./g) || []).length > 1) {
                          return;
                        }

                        setFieldValue(
                          "finantial_info_exportation_percentage",
                          value
                        );
                      }}
                    />
                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_suppliers_cost"
                      label="Custo com Fornecedores"
                      placeholder="Digite"
                      value={values.finantial_info_suppliers_cost}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_suppliers_cost",
                          e.target.value
                        )
                      }
                    />

                    <Input
                      max={6}
                      maxLength={6}
                      size="large"
                      name="finantial_info_number_of_employees"
                      label="Número de Empregados"
                      placeholder="Digite"
                      value={values.finantial_info_number_of_employees}
                      onChange={(e) => {
                        let value = e.target.value;
                        value = value.replace(/[^\d]/g, "");

                        setFieldValue(
                          "finantial_info_number_of_employees",
                          value
                        );
                      }}
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_payroll_value"
                      label="Valor da Folha de Pagamento"
                      placeholder="Digite"
                      value={values.finantial_info_payroll_value}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_payroll_value",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_tax_cost"
                      label="Custo tributário"
                      placeholder="Digite"
                      value={values.finantial_info_tax_cost}
                      onChange={(e) =>
                        setFieldValue("finantial_info_tax_cost", e.target.value)
                      }
                    />

                    <Input
                      size="large"
                      name="finantial_info_headquarters_location"
                      label="Localização da Sede"
                      placeholder="Digite"
                      value={values.finantial_info_headquarters_location}
                      onChange={(e) =>
                        setFieldValue(
                          "finantial_info_headquarters_location",
                          e.target.value
                        )
                      }
                    />

                    <InputCurrency
                      max={21}
                      maxLength={21}
                      size="medium"
                      name="finantial_info_pmv"
                      label="PMV"
                      placeholder="Digite"
                      value={values.finantial_info_pmv}
                      onChange={(e) =>
                        setFieldValue("finantial_info_pmv", e.target.value)
                      }
                    />

                    <Input
                      size="large"
                      name="finantial_info_funding_potential"
                      label="Potencial de Captação (%)"
                      placeholder="Digite"
                      value={values.finantial_info_funding_potential}
                      max={6}
                      maxLength={6}
                      onChange={(e) => {
                        let value = e.target.value;
                        value = value.replace(/[^\d.]/g, "");

                        const hasDot = value.includes(".");
                        if (hasDot && (value.match(/\./g) || []).length > 1) {
                          return;
                        }

                        setFieldValue(
                          "finantial_info_funding_potential",
                          value
                        );
                      }}
                    />

                    <Input
                      size="large"
                      name="finantial_info_late_sales_percentage"
                      label="Percentual de Vendas em Atraso (%)"
                      placeholder="Digite"
                      value={values.finantial_info_late_sales_percentage}
                      max={6}
                      maxLength={6}
                      onChange={(e) => {
                        let value = e.target.value;
                        value = value.replace(/[^\d.]/g, "");

                        const hasDot = value.includes(".");
                        if (hasDot && (value.match(/\./g) || []).length > 1) {
                          return;
                        }

                        setFieldValue(
                          "finantial_info_late_sales_percentage",
                          value
                        );
                      }}
                    />
                  </>
                </div>
                <Margin mt={50} />
              </>
            </>
          )}
        </>

        {!canDoSimulations && (
          <>
            <div>
              <Margin mt={70} />
              <TagLup src={lockIcon} />
              <Margin mt={30} />
              <Text>Acesso restrito</Text>

              <Subtext>
                Você não tem permissão para ~visualizar as simulações <br />{" "}
                contate o administrador do sistema para mais informações.
              </Subtext>
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default NewContactCompany;
